import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Sparkles,
  Palette,
  Video,
  Film,
  Camera,
  PenTool,
  Megaphone,
  Building,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Zap,
  Layers,
  Sparkle,
  Compass,
  FileText,
  Briefcase,
  Play,
  Monitor,
  Eye,
  Check,
  BarChart3,
  Lightbulb,
  MessageSquare,
  Layout,
  Tv,
  Printer,
  Radio,
  Image as ImageIcon,
  Sliders,
  Send,
  Building2,
  Sparkles as SparklesIcon,
  Search,
  PieChart,
  Grid,
} from "lucide-react";

export const Route = createFileRoute("/services_/branding-creatives")({
  head: () => ({
    meta: [
      {
        title:
          "Branding & Creative Services — KMWAdvertising LLP | Brand Strategy, Logo Design, AI Video, Photography & Campaign Design",
      },
      {
        name: "description",
        content:
          "Build memorable brands and drive business growth with KMWAdvertising LLP. Over 25 years of experience in Brand Strategy, Logo Design, Visual Identity, Motion Graphics, AI Video Creation, Video Production, Photography, Copywriting, 360-Degree Campaign Design, and Corporate Office Branding.",
      },
      {
        property: "og:title",
        content: "Branding & Creative Services — KMWAdvertising LLP",
      },
      {
        property: "og:description",
        content:
          "Transform your brand into an experience people remember. Complete brand ecosystems combining strategic thinking, compelling storytelling, and outstanding visual design.",
      },
    ],
  }),
  component: BrandingCreativesServicePage,
});

export function BrandingCreativesServicePage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedService, setSelectedService] = useState("Brand Strategy & Identity");

  return (
    <div className="min-h-screen bg-[#07070d] text-slate-100 selection:bg-pink-500 selection:text-white font-sans relative overflow-x-hidden">
      <SiteNav />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-gradient-to-tr from-pink-600/20 via-purple-600/15 to-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
        <div className="absolute top-10 right-10 w-96 h-96 bg-pink-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="container-x relative z-10">
          <div className="flex items-center gap-2 text-sm font-mono font-medium text-slate-300 mb-6">
            <Link to="/" className="hover:text-white transition">
              Home
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            <Link to="/services" className="hover:text-white transition">
              Services
            </Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
            <span className="text-pink-400 font-semibold">Branding & Creatives</span>
          </div>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase tracking-wider mb-6">
              <Sparkles className="h-3.5 w-3.5" />
              Branding & Creatives
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.12] mb-6">
              Branding & Creative Services That Build{" "}
              <span className="bg-gradient-to-r from-pink-400 via-purple-300 to-amber-300 bg-clip-text text-transparent">
                Memorable Brands
              </span>{" "}
              and Drive Business Growth
            </h1>

            <h2 className="text-xl sm:text-2xl font-bold text-pink-300 mb-6">
              Transform Your Brand into an Experience People Remember
            </h2>

            <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed mb-8 max-w-3xl">
              <p>
                In today's highly competitive marketplace, products can be copied, services can be matched, and pricing can change overnight. What truly differentiates a business is its brand. A powerful brand creates emotional connections, inspires trust and influences customer decisions long before a purchase is made.
              </p>
              <p>
                Your logo, colours, typography, imagery, videos, advertising campaigns and messaging all work together to shape how customers perceive your business. Every interaction contributes to your brand story.
              </p>
              <p>
                At KMWAdvertising LLP, we believe branding goes far beyond designing a logo. We create complete brand ecosystems that combine strategic thinking, compelling storytelling and outstanding visual design. With over 25 years of experience in advertising, media planning, creative strategy and digital marketing, we help businesses establish distinctive identities that stand out across every customer touchpoint.
              </p>
              <p>
                From startups launching their first brand to established organisations refreshing their identity, we deliver creative solutions that strengthen recognition, improve customer engagement and support long-term business growth.
              </p>
              <p>
                Whether you require a complete brand identity, logo design, marketing collateral, AI-generated videos, television commercials, product photography or integrated advertising campaigns, our creative team transforms ideas into meaningful brand experiences.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#strategy-form"
                className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-600 to-amber-500 text-white font-bold text-sm shadow-[0_0_30px_rgba(236,72,153,0.35)] hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] hover:scale-[1.02] transition-all"
              >
                <span>Book Branding Strategy Session</span>
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#all-services-breakdown"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 hover:border-white/20 transition-all"
              >
                <span>Explore All Creative Services</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Branding Matters */}
      <section className="py-16 md:py-24 bg-[#0a0a14] border-y border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
            <h2 className="text-xs font-mono font-bold uppercase tracking-widest text-pink-400">
              Strategic Asset
            </h2>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white">
              Why Branding Matters
            </h3>
            <p className="text-slate-300 text-sm sm:text-base">
              Your brand is one of your most valuable business assets. It influences how customers perceive your organisation and determines whether they choose your products or services over competitors.
            </p>
            <p className="text-pink-300 font-semibold text-sm">
              An effective brand helps businesses:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center mb-4">
                  <ShieldCheck className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Build Trust and Credibility</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Professional branding creates confidence and demonstrates consistency across every communication channel.
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-pink-500/10 border border-pink-500/20 text-pink-400 flex items-center justify-center mb-4">
                  <Eye className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Increase Brand Recognition</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Distinctive visual identities help customers recognise and remember your business more easily.
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mb-4">
                  <TrendingUp className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Support Marketing Success</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Strong branding improves the effectiveness of advertising, social media, SEO, performance marketing and content campaigns.
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center mb-4">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Create Emotional Connections</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  People connect with stories and experiences more than products. Great branding communicates purpose, values and personality.
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-6 flex flex-col justify-between">
              <div>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mb-4">
                  <Users className="h-5 w-5" />
                </div>
                <h4 className="text-base font-bold text-white mb-2">Improve Customer Loyalty</h4>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Consistent brand experiences encourage repeat business and long-term customer relationships.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Branding & Creative Services (Full 20-item list) */}
      <section id="all-services-breakdown" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
              Our Branding & Creative Services
            </h2>
            <p className="text-slate-300 text-sm sm:text-base">
              KMWAdvertising LLP provides complete creative solutions designed to strengthen every aspect of your brand. Our integrated services include:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-8">
            {[
              "Brand Strategy",
              "Brand Positioning",
              "Logo Design",
              "Corporate Identity",
              "Visual Identity Systems",
              "Brand Guidelines",
              "Packaging Design",
              "Marketing Collateral",
              "Motion Graphics",
              "AI Video Creation",
              "Commercial Video Production",
              "Product Photography",
              "Corporate Photography",
              "Copywriting",
              "Advertising Campaign Design",
              "Social Media Creative Design",
              "Digital Content Production",
              "Print Creative Design",
              "Presentation Design",
              "Creative Consulting",
            ].map((service) => (
              <div key={service} className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs font-semibold text-slate-200 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-pink-400 shrink-0" />
                <span>{service}</span>
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-slate-400 italic">
            Each project is developed through collaboration between strategists, designers, copywriters, photographers, filmmakers and digital marketing specialists.
          </p>
        </div>
      </section>

      {/* Detailed Section 1: Brand Strategy */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-mono font-bold uppercase">
              <Compass className="h-3.5 w-3.5" />
              Brand Strategy
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Every Great Brand Begins with Strategy
            </h2>
            <div className="p-4 rounded-xl bg-white/5 border-l-4 border-amber-400 text-slate-200 text-sm italic font-serif">
              "Design without strategy creates attractive visuals. Strategy creates meaningful brands."
            </div>
            <p className="text-slate-300 text-sm sm:text-base">
              Before developing creative assets, we take time to understand:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <h3 className="text-base font-bold text-white mb-2">Business Vision</h3>
              <p className="text-xs text-slate-400">What are your long-term objectives?</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <h3 className="text-base font-bold text-white mb-2">Market Position</h3>
              <p className="text-xs text-slate-400">How do customers currently perceive your brand?</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <h3 className="text-base font-bold text-white mb-2">Competitor Landscape</h3>
              <p className="text-xs text-slate-400">What opportunities exist to differentiate your business?</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <h3 className="text-base font-bold text-white mb-2">Customer Behaviour</h3>
              <p className="text-xs text-slate-400">What motivates your ideal audience?</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <h3 className="text-base font-bold text-white mb-2">Brand Personality</h3>
              <p className="text-xs text-slate-400">How should customers feel when interacting with your business?</p>
            </div>
          </div>

          <p className="text-xs font-mono text-slate-400">
            These insights guide every creative decision and ensure consistency across all communication channels.
          </p>
        </div>
      </section>

      {/* Detailed Section 2: Logo Design */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <PenTool className="h-3.5 w-3.5" />
              Logo Design
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Creating Logos That Become Business Assets
            </h2>
            <p className="text-slate-300 text-sm sm:text-base">
              A logo is often the first visual element customers associate with your business. An effective logo should be memorable, scalable and timeless.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Our logo design process includes:</h3>
              <ul className="space-y-3 text-xs text-slate-300">
                <li className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white block text-sm">Discovery</strong>
                  Understanding your business, audience and competitors.
                </li>
                <li className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white block text-sm">Concept Development</strong>
                  Exploring multiple creative directions.
                </li>
                <li className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white block text-sm">Refinement</strong>
                  Selecting and refining the strongest concepts.
                </li>
                <li className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white block text-sm">Final Identity</strong>
                  Delivering logos suitable for digital, print and large-format applications.
                </li>
              </ul>
            </div>

            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Deliverables include:</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-slate-300">
                {[
                  "Primary Logo",
                  "Secondary Logo",
                  "Icon Version",
                  "Monochrome Versions",
                  "Colour Variations",
                  "Social Media Versions",
                  "Print Files",
                  "Vector Artwork",
                  "Brand Usage Guidelines",
                ].map((del) => (
                  <div key={del} className="flex items-center gap-2 p-2.5 rounded-lg bg-white/5 border border-white/10">
                    <Check className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>{del}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-xs font-mono text-slate-400">
            Rather than following short-term design trends, we create logos designed to remain relevant for years.
          </p>
        </div>
      </section>

      {/* Detailed Section 3: Visual Identity & Brand Guidelines */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Visual Identity */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
                <Palette className="h-3.5 w-3.5" />
                Visual Identity
              </div>
              <h2 className="text-3xl font-extrabold text-white">
                Build a Consistent Brand Across Every Platform
              </h2>
              <p className="text-slate-300 text-sm">
                Your visual identity extends beyond your logo. It includes every design element customers encounter, including:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Colour Palette",
                  "Typography",
                  "Iconography",
                  "Photography Style",
                  "Illustration Style",
                  "Graphic Elements",
                  "Layout Systems",
                  "Brand Patterns",
                  "Presentation Templates",
                  "Social Media Templates",
                ].map((item) => (
                  <div key={item} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-mono text-slate-400">
                A consistent visual identity strengthens recognition and creates a more professional customer experience.
              </p>
            </div>

            {/* Brand Guidelines */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-mono font-bold uppercase">
                <FileText className="h-3.5 w-3.5" />
                Brand Guidelines
              </div>
              <h2 className="text-3xl font-extrabold text-white">
                Protect Your Brand Consistency
              </h2>
              <p className="text-slate-300 text-sm">
                As organisations grow, maintaining consistency becomes increasingly important. Our Brand Guidelines typically include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Logo Usage Rules",
                  "Colour Specifications",
                  "Typography Standards",
                  "Photography Direction",
                  "Tone of Voice",
                  "Illustration Style",
                  "Social Media Templates",
                  "Advertising Standards",
                  "Print Specifications",
                  "Digital Guidelines",
                ].map((item) => (
                  <div key={item} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-mono text-slate-400">
                These standards ensure every communication reflects your brand accurately regardless of who creates it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Creative That Performs Across Every Channel */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Creative That Performs Across Every Channel
            </h2>
            <p className="text-slate-300 text-sm sm:text-base">
              Unlike traditional design studios, KMWAdvertising LLP combines creativity with media expertise. Every creative asset is developed with its intended platform in mind, including:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
            {[
              "Television Advertising",
              "Print Advertising",
              "Outdoor Media",
              "Cinema Advertising",
              "Google Ads",
              "Meta Ads",
              "LinkedIn Campaigns",
              "Programmatic Advertising",
              "OTT Campaigns",
              "Social Media Marketing",
              "Email Marketing",
              "Websites",
              "Landing Pages",
            ].map((channel) => (
              <div key={channel} className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs font-medium text-slate-200 text-center">
                {channel}
              </div>
            ))}
          </div>

          <p className="text-xs font-mono text-slate-400 leading-relaxed max-w-4xl">
            Because our creative team works alongside media planners and performance marketers, every design is developed to maximise engagement, improve campaign effectiveness and support measurable business objectives.
          </p>
        </div>
      </section>

      {/* Detailed Section 4: Motion Graphics */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-mono font-bold uppercase">
              <Film className="h-3.5 w-3.5" />
              Motion Graphics
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Bring Your Brand to Life with Engaging Motion Graphics
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Static visuals capture attention, but motion creates impact. Motion graphics combine animation, typography, illustrations, icons and visual effects to simplify complex ideas, strengthen brand storytelling and improve audience engagement across digital platforms.
              </p>
              <p>
                Today's audiences consume content rapidly across social media, websites, OTT platforms and digital advertising. Professionally designed motion graphics help brands communicate messages more effectively while increasing watch time, click-through rates and brand recall.
              </p>
              <p>
                At KMWAdvertising LLP, we create visually compelling motion graphics that blend creativity with strategic communication. Every animation is designed to align with your brand identity and marketing objectives, ensuring consistency across all digital and traditional media channels.
              </p>
            </div>
          </div>

          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6 mb-8">
            <h3 className="text-xl font-bold text-white">Our Motion Graphics Services Include:</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs text-slate-300">
              {[
                "Brand Intro & Logo Animation",
                "Social Media Motion Creatives",
                "Animated Advertisements",
                "Corporate Explainer Videos",
                "Product Feature Animations",
                "Kinetic Typography Videos",
                "Infographics & Data Visualisation",
                "Event Openers & LED Screen Content",
                "Presentation Animations",
                "GIFs & Micro Animations",
                "Website Motion Graphics",
                "Digital Display & DOOH Animations",
                "YouTube Intro & Outro Animations",
                "Animated Reels & Shorts",
                "Motion Graphics for OTT & Television Commercials",
              ].map((item) => (
                <div key={item} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-xs font-mono text-slate-400">
            Whether you're launching a product, explaining a service or enhancing a marketing campaign, our motion graphics are designed to capture attention within the first few seconds and keep audiences engaged throughout.
          </p>
        </div>
      </section>

      {/* Detailed Section 5: AI Video Creation */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Zap className="h-3.5 w-3.5" />
              AI Video Creation (Long-form & Short-form)
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Scale Content Production with AI-Powered Video Creation
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Artificial Intelligence is transforming content production by enabling brands to create high-quality videos faster, more efficiently and at scale. AI-powered video creation allows businesses to produce personalised marketing content without compromising creativity or quality.
              </p>
              <p>
                At KMWAdvertising LLP, we combine AI technology with creative direction, storytelling and human expertise to deliver videos that feel authentic, engaging and brand-consistent.
              </p>
              <p>
                Rather than replacing creativity, AI enhances production by accelerating scripting, editing, voice generation, subtitles, localisation and content adaptation across multiple platforms.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Long Form */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Long-Form AI Videos</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Long-form videos help businesses educate audiences, establish authority and build trust. We create:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Corporate Films",
                  "Brand Story Videos",
                  "Product Demonstrations",
                  "Educational Videos",
                  "Company Profile Videos",
                  "Customer Success Stories",
                  "Case Study Videos",
                  "Webinar Videos",
                  "Training & E-learning Content",
                  "Investor Presentations",
                  "Documentary-style Brand Films",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Long-form videos are ideal for websites, YouTube, presentations, trade shows and corporate communications.
              </p>
            </div>

            {/* Short Form */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Short-Form AI Videos</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Short-form content dominates today's digital landscape. Platforms like Instagram Reels, YouTube Shorts, Facebook, LinkedIn and TikTok reward engaging, concise video formats that deliver immediate value. Our short-form AI video services include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Instagram Reels",
                  "YouTube Shorts",
                  "LinkedIn Video Content",
                  "Facebook Video Ads",
                  "Product Promotions",
                  "Festival Campaigns",
                  "Event Highlights",
                  "Motion Posters",
                  "Promotional Teasers",
                  "Testimonial Videos",
                  "Explainer Snippets",
                  "AI Avatar Videos",
                  "AI Voice-over Videos",
                  "Captioned Social Videos",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-pink-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Every video is optimised for platform-specific dimensions, audience behaviour and algorithm preferences to maximise reach and engagement.
              </p>
            </div>
          </div>

          {/* AI Capabilities */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
            <h3 className="text-xl font-bold text-white">AI Video Capabilities</h3>
            <p className="text-xs text-slate-400">Our AI-powered production workflow includes:</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-xs text-slate-300">
              {[
                "AI Script Assistance",
                "AI Voice Generation",
                "AI Avatars",
                "Automated Subtitle Generation",
                "Multi-language Dubbing",
                "AI Video Editing",
                "Background Removal",
                "Scene Enhancement",
                "AI Image Generation",
                "AI Motion Graphics",
                "Content Repurposing",
                "Video Localisation",
              ].map((cap) => (
                <div key={cap} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400 shrink-0" />
                  <span>{cap}</span>
                </div>
              ))}
            </div>
            <p className="text-xs font-mono text-slate-400 pt-2">
              By combining AI with creative expertise, we help brands produce more content in less time while maintaining professional quality.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Section 6: Video Production */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono font-bold uppercase">
              <Video className="h-3.5 w-3.5" />
              Video Production
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              High-Quality Video Production That Tells Powerful Brand Stories
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Video has become one of the most influential communication formats across digital marketing, television, OTT platforms and social media. It enables businesses to educate, inspire, entertain and persuade audiences more effectively than almost any other medium.
              </p>
              <p>
                At KMWAdvertising LLP, we offer complete end-to-end video production services, managing every stage from concept development to final delivery.
              </p>
              <p>
                Our experienced creative team collaborates with directors, cinematographers, editors, scriptwriters, animators and digital strategists to produce visually compelling videos that align with your marketing goals.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Pre-Production */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white text-purple-300">Pre-Production</h3>
              <p className="text-xs text-slate-400">Every successful video begins with meticulous planning. Our pre-production services include:</p>
              <div className="space-y-2 text-xs text-slate-300">
                {[
                  "Creative Ideation",
                  "Concept Development",
                  "Scriptwriting",
                  "Storyboarding",
                  "Mood Boards",
                  "Casting Support",
                  "Location Scouting",
                  "Production Planning",
                  "Budgeting",
                  "Shot Planning",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Production */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white text-purple-300">Production</h3>
              <p className="text-xs text-slate-400">Using professional equipment and experienced production crews, we capture cinematic visuals that reflect your brand's quality and professionalism. Our production capabilities include:</p>
              <div className="space-y-2 text-xs text-slate-300">
                {[
                  "Corporate Shoots",
                  "Television Commercials (TVC)",
                  "Digital Commercials",
                  "Product Videos",
                  "Manufacturing Videos",
                  "Factory Tours",
                  "Event Coverage",
                  "Drone Videography",
                  "Interviews",
                  "Testimonials",
                  "Lifestyle Shoots",
                  "Brand Films",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Post-Production */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white text-purple-300">Post-Production</h3>
              <p className="text-xs text-slate-400">Post-production transforms raw footage into engaging visual stories. Services include:</p>
              <div className="space-y-2 text-xs text-slate-300">
                {[
                  "Professional Editing",
                  "Motion Graphics",
                  "Colour Grading",
                  "Sound Design",
                  "Voice-over Recording",
                  "Music Licensing",
                  "Visual Effects (VFX)",
                  "AI-assisted Editing",
                  "Subtitles & Closed Captions",
                  "Multi-language Versions",
                  "Platform Optimisation",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-purple-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Each video is delivered in formats optimised for websites, YouTube, television, social media, OTT platforms and digital advertising.
              </p>
            </div>
          </div>

          {/* Types of Videos */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
            <h3 className="text-xl font-bold text-white">Types of Videos We Produce</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 text-xs text-slate-300">
              {[
                "Brand Films",
                "Corporate Videos",
                "Product Launch Videos",
                "Explainer Videos",
                "Recruitment Videos",
                "Manufacturing Videos",
                "Testimonial Videos",
                "CEO Messages",
                "Event Videos",
                "CSR Films",
                "Training Videos",
                "Educational Content",
                "Social Media Videos",
                "Advertising Commercials",
                "Documentary Films",
              ].map((v) => (
                <div key={v} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                  <span>{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs font-mono text-slate-400 pt-2">
              Our objective is not simply to create visually appealing videos, but to produce content that supports brand awareness, customer engagement and measurable business outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Section 7: Corporate & Product Photography */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-mono font-bold uppercase">
              <Camera className="h-3.5 w-3.5" />
              Corporate Photography & Product Photography
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Professional Photography That Builds Trust and Elevates Your Brand
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Photography is often the first visual impression customers have of your business. High-quality imagery enhances credibility, improves marketing performance and creates a stronger emotional connection with your audience.
              </p>
              <p>
                At KMWAdvertising LLP, we provide professional photography services designed to support websites, advertising campaigns, social media, brochures, annual reports and eCommerce platforms.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Product Photography */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Product Photography</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Exceptional product photography increases customer confidence and improves online conversions. Our product photography services include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "eCommerce Product Photography",
                  "Catalogue Photography",
                  "White Background Photography",
                  "Lifestyle Product Photography",
                  "Fashion Photography",
                  "Jewellery Photography",
                  "Food Photography",
                  "FMCG Photography",
                  "Packaging Photography",
                  "Industrial Product Photography",
                  "Automotive Photography",
                  "360-Degree Product Imaging",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Every photograph is professionally lit, colour-corrected and retouched to ensure consistency across digital and print applications.
              </p>
            </div>

            {/* Photography Post-Production */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Photography Post-Production</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Our editing services include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Colour Correction",
                  "Retouching",
                  "Background Removal",
                  "Shadow Enhancement",
                  "Reflection Creation",
                  "Image Optimisation",
                  "Web Compression",
                  "Print Optimisation",
                  "AI Image Enhancement",
                  "Batch Editing",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Professionally edited images ensure your brand maintains a premium appearance across every customer touchpoint.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 8: Creative Copywriting Services */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-mono font-bold uppercase">
              <PenTool className="h-3.5 w-3.5" />
              Creative Copywriting Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Words That Inspire Action and Build Stronger Brands
            </h2>
            <div className="p-4 rounded-xl bg-white/5 border-l-4 border-amber-400 text-slate-200 text-sm italic font-serif">
              "Exceptional design captures attention. Exceptional copy inspires action."
            </div>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Every successful brand relies on clear, persuasive and emotionally engaging communication. Whether you're launching a product, running a campaign or redesigning your website, compelling copy helps translate your brand's value into messages that resonate with your audience.
              </p>
              <p>
                At KMWAdvertising LLP, our copywriters combine marketing psychology, storytelling, SEO and brand strategy to craft content that informs, persuades and converts.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Brand Copywriting */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-lg font-bold text-white text-amber-300">Brand Copywriting</h3>
              <p className="text-xs text-slate-400">We help businesses define a distinctive voice that reflects their personality and values. Our brand writing services include:</p>
              <div className="space-y-2 text-xs text-slate-300">
                {[
                  "Brand Messaging",
                  "Brand Story Development",
                  "Vision & Mission Statements",
                  "Brand Positioning Statements",
                  "Taglines & Slogans",
                  "Tone of Voice Guidelines",
                  "Corporate Profiles",
                  "Company Presentations",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Marketing Copywriting */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-lg font-bold text-white text-amber-300">Marketing Copywriting</h3>
              <p className="text-xs text-slate-400">Our marketing content is designed to improve engagement and campaign performance across every channel. Services include:</p>
              <div className="space-y-2 text-xs text-slate-300 max-h-[360px] overflow-y-auto pr-1 custom-scrollbar">
                {[
                  "Website Copywriting",
                  "Landing Pages",
                  "SEO Content",
                  "Google Ads Copy",
                  "Meta Ads Copy",
                  "LinkedIn Campaign Copy",
                  "Email Marketing Content",
                  "Product Descriptions",
                  "Sales Brochures",
                  "Print Advertisements",
                  "Outdoor Advertising Copy",
                  "Radio Scripts",
                  "Television Commercial Scripts",
                  "Video Scripts",
                  "Social Media Captions",
                  "Press Releases",
                  "Case Studies",
                  "Whitepapers",
                  "Blog Articles",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* SEO Copywriting */}
            <div className="p-7 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-lg font-bold text-white text-amber-300">SEO Copywriting</h3>
              <p className="text-xs text-slate-400">Content is created not only for readers but also for search engines. Our SEO copywriting includes:</p>
              <div className="space-y-2 text-xs text-slate-300">
                {[
                  "Keyword Research",
                  "Search Intent Analysis",
                  "Optimised Headings",
                  "Internal Linking",
                  "Semantic Keyword Integration",
                  "EEAT Optimisation",
                  "Featured Snippet Optimisation",
                  "AI Search Optimisation",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Every piece of content is developed to improve visibility, readability and conversions while maintaining a natural, human-first writing style.
              </p>
            </div>
          </div>

          {/* Why Choose Our Creative Team */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
            <h3 className="text-xl font-bold text-white">Why Choose Our Creative Team?</h3>
            <p className="text-xs text-slate-300 leading-relaxed">
              At KMWAdvertising LLP, creativity is backed by strategy, media expertise and performance insights. Unlike standalone creative studios, our designers, writers, photographers, filmmakers and digital marketers collaborate from the beginning of every project. This integrated approach ensures that every logo, video, photograph, animation and piece of copy is not only visually compelling but also aligned with your business objectives, audience behaviour and marketing channels, delivering creative work that drives measurable brand growth rather than simply looking impressive.
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Section 9: Campaign Design Services */}
      <section className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-300 text-xs font-mono font-bold uppercase">
              <Megaphone className="h-3.5 w-3.5" />
              Campaign Design Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Integrated Campaign Design That Creates Impact Across Every Touchpoint
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                In today's fragmented media landscape, customers interact with brands across multiple platforms before making a purchase decision. A consumer may first discover your brand through social media, see a display advertisement, watch a YouTube video, receive an email, visit your website, encounter an outdoor billboard and finally complete a purchase after seeing a remarketing campaign.
              </p>
              <p>
                This complex customer journey requires more than individual advertisements. It demands a cohesive, strategically planned campaign that delivers a consistent message across every channel.
              </p>
              <p>
                At KMWAdvertising LLP, we specialise in 360-degree Campaign Design, developing integrated marketing campaigns that connect creativity, strategy and media planning to maximise brand awareness, audience engagement and business results. With over 25 years of experience in advertising, digital marketing and media buying, we create campaigns that are not only visually compelling but also aligned with measurable marketing objectives.
              </p>
              <p>
                Whether you're launching a new brand, introducing a product, promoting an event, driving seasonal sales or increasing market share, our campaign design team develops ideas that resonate with audiences and perform across traditional and digital media.
              </p>
            </div>
          </div>

          <div className="space-y-8 mb-12">
            {/* Strategic Campaign Planning */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Strategic Campaign Planning</h3>
              <h4 className="text-sm font-semibold text-pink-300">Every Successful Campaign Begins with Insight</h4>
              <p className="text-xs text-slate-400">
                Effective campaigns are built on research and strategy, not assumptions. Before developing creative concepts, we invest time in understanding your business, industry and audience. Our campaign planning process includes:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 text-xs text-slate-300">
                {[
                  "Business Objective Analysis",
                  "Brand Positioning Review",
                  "Audience Research",
                  "Consumer Behaviour Analysis",
                  "Competitor Benchmarking",
                  "Market Trend Analysis",
                  "Media Consumption Insights",
                  "Customer Journey Mapping",
                  "Communication Objectives",
                  "Campaign KPI Definition",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-pink-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                This strategic foundation ensures that every campaign delivers relevant messaging to the right audience at the right time.
              </p>
            </div>

            {/* Creative Campaign Development */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Creative Campaign Development</h3>
              <h4 className="text-sm font-semibold text-pink-300">Powerful Ideas That Inspire Action</h4>
              <p className="text-xs text-slate-400">
                The strongest campaigns are driven by a central creative concept that can be adapted across multiple platforms while maintaining consistency. Our creative development services include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2 text-xs text-slate-300">
                {[
                  "Campaign Concept Creation",
                  "Creative Direction",
                  "Key Visual Design",
                  "Campaign Messaging",
                  "Brand Storytelling",
                  "Advertising Themes",
                  "Visual Identity Development",
                  "Creative Mood Boards",
                  "Campaign Taglines",
                  "Campaign Slogans",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-pink-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                Every concept is designed to strengthen brand recall, create emotional connections and encourage audience engagement.
              </p>
            </div>

            {/* Multi-Channel Campaign Execution */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Multi-Channel Campaign Execution</h3>
              <h4 className="text-sm font-semibold text-pink-300">One Campaign. Multiple Platforms. Consistent Experience.</h4>
              <p className="text-xs text-slate-400">
                Today's consumers move seamlessly between online and offline media. We design campaigns that maintain visual consistency and messaging across every customer touchpoint. Our campaign assets can be developed for:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                  <strong className="text-white text-xs block text-pink-400">Digital Advertising</strong>
                  <ul className="text-[11px] text-slate-300 space-y-1">
                    <li>• Google Ads</li>
                    <li>• Meta (Facebook & Instagram) Ads</li>
                    <li>• LinkedIn Advertising</li>
                    <li>• YouTube Campaigns</li>
                    <li>• Programmatic Display</li>
                    <li>• Native Advertising</li>
                    <li>• OTT Advertising</li>
                    <li>• Mobile App Advertising</li>
                  </ul>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                  <strong className="text-white text-xs block text-pink-400">Social Media</strong>
                  <ul className="text-[11px] text-slate-300 space-y-1">
                    <li>• Instagram Creatives</li>
                    <li>• Facebook Campaigns</li>
                    <li>• LinkedIn Content</li>
                    <li>• X (Twitter) Creatives</li>
                    <li>• YouTube Assets</li>
                    <li>• Short-form Video Content</li>
                    <li>• Stories & Reels</li>
                    <li>• Carousel Creatives</li>
                  </ul>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                  <strong className="text-white text-xs block text-pink-400">Traditional Media</strong>
                  <ul className="text-[11px] text-slate-300 space-y-1">
                    <li>• Television Commercials</li>
                    <li>• Print Advertisements</li>
                    <li>• Newspaper Campaigns</li>
                    <li>• Magazine Advertising</li>
                    <li>• Radio Campaigns</li>
                    <li>• Outdoor Advertising</li>
                    <li>• Transit Media</li>
                    <li>• Cinema Advertising</li>
                  </ul>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-2">
                  <strong className="text-white text-xs block text-pink-400">Brand Collateral</strong>
                  <ul className="text-[11px] text-slate-300 space-y-1">
                    <li>• Brochures</li>
                    <li>• Flyers</li>
                    <li>• Posters</li>
                    <li>• Product Catalogues</li>
                    <li>• Event Branding</li>
                    <li>• Exhibition Graphics</li>
                    <li>• Retail Branding</li>
                    <li>• Point-of-Sale (POS) Materials</li>
                  </ul>
                </div>
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                This integrated approach ensures customers receive a unified brand experience regardless of where they interact with your business.
              </p>
            </div>

            {/* Campaign Types We Design */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Campaign Types We Design</h3>
              <p className="text-xs text-slate-400">Every campaign is tailored to specific business objectives and customer journeys. Our expertise includes:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Brand Awareness Campaigns</strong>
                  <p className="text-xs text-slate-400">Increase visibility and strengthen brand recognition through memorable storytelling and consistent messaging.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Product Launch Campaigns</strong>
                  <p className="text-xs text-slate-400">Introduce new products or services with coordinated multi-channel campaigns that generate excitement and drive market adoption.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Seasonal Campaigns</strong>
                  <p className="text-xs text-slate-400">Maximise engagement during festivals, holidays and shopping seasons with timely, relevant creative concepts.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Performance Marketing Campaigns</strong>
                  <p className="text-xs text-slate-400">Develop conversion-focused creative assets for Google Ads, Meta Ads, programmatic advertising and landing pages that drive measurable business outcomes.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Corporate Campaigns</strong>
                  <p className="text-xs text-slate-400">Promote organisational achievements, CSR initiatives, recruitment drives and investor communications with professional, brand-aligned campaigns.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <strong className="text-white text-sm block mb-1">Retail & Promotional Campaigns</strong>
                  <p className="text-xs text-slate-400">Drive footfall, online sales and customer engagement through tactical promotional campaigns, limited-time offers and retail activations.</p>
                </div>
                <div className="p-4 rounded-xl bg-white/5 border border-white/10 md:col-span-2 lg:col-span-3">
                  <strong className="text-white text-sm block mb-1">Event & Exhibition Campaigns</strong>
                  <p className="text-xs text-slate-400">Create cohesive branding for conferences, trade shows, product launches and experiential marketing events.</p>
                </div>
              </div>
            </div>

            {/* Campaign Optimisation & Performance Analysis */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Campaign Optimisation & Performance Analysis</h3>
              <h4 className="text-sm font-semibold text-pink-300">Creativity Backed by Data</h4>
              <p className="text-xs text-slate-400">
                A successful campaign doesn't end with launch. Continuous optimisation ensures that creative assets remain effective throughout the campaign lifecycle. We evaluate:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs text-slate-300">
                {[
                  "Campaign Reach",
                  "Audience Engagement",
                  "Click-Through Rate (CTR)",
                  "Conversion Rate",
                  "Lead Quality",
                  "Cost per Acquisition (CPA)",
                  "Return on Advertising Spend (ROAS)",
                  "Brand Lift",
                  "Social Engagement",
                  "Creative Performance by Platform",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-mono text-slate-400 pt-2 border-t border-white/10">
                By combining creative expertise with analytics and performance marketing, we refine campaigns to maximise efficiency and improve return on investment.
              </p>
            </div>

            {/* Why Choose KMWAdvertising LLP for Campaign Design? */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Why Choose KMWAdvertising LLP for Campaign Design?</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                A successful advertising campaign requires more than attractive visuals. It requires strategic thinking, market insight, creative excellence and media expertise. With over 25 years of advertising experience, KMWAdvertising LLP delivers integrated campaigns that combine creativity with measurable business outcomes. Our strengths include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "360-degree integrated campaign planning",
                  "Award-worthy creative concepts backed by strategy",
                  "In-house design, copywriting, photography and video production",
                  "Digital-first and traditional media expertise",
                  "Performance-focused creative development",
                  "AI-enhanced content production and optimisation",
                  "Cross-channel campaign consistency",
                  "Data-driven campaign measurement and reporting",
                  "End-to-end execution from concept to launch",
                  "Scalable solutions for startups, SMEs and enterprise brands",
                ].map((str) => (
                  <div key={str} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                    <Check className="h-4 w-4 text-pink-400 shrink-0" />
                    <span>{str}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-mono text-slate-400 pt-2">
                Whether you're launching a new brand, expanding into new markets or running a nationwide advertising campaign, we design creative experiences that capture attention, strengthen brand perception and deliver lasting business impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Section 10: Corporate Office Branding Services */}
      <section className="py-20 md:py-28 bg-[#0a0a14] border-t border-white/10 relative">
        <div className="container-x">
          <div className="max-w-3xl mb-12 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-mono font-bold uppercase">
              <Building2 className="h-3.5 w-3.5" />
              Corporate Office Branding Services
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
              Transform Your Workplace into a Powerful Brand Experience
            </h2>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Your office is more than just a workplace. It is a physical representation of your brand, culture and values. Every visitor, client, employee and business partner who walks through your doors forms an impression based on the environment they experience. From reception areas and meeting rooms to workspaces and breakout zones, every element contributes to how your brand is perceived.
              </p>
              <p>
                A professionally branded office strengthens brand identity, enhances employee engagement and creates memorable experiences for customers and stakeholders. Whether you're welcoming potential clients, conducting investor meetings or onboarding new employees, your office should consistently reflect the professionalism and personality of your organisation.
              </p>
              <p>
                At KMWAdvertising LLP, we provide end-to-end Corporate Office Branding Services that combine strategic branding, interior graphics, environmental design and visual communication to transform workplaces into inspiring brand environments. With over 25 years of experience in branding, advertising and design, we create office spaces that reinforce your corporate identity while improving functionality and aesthetics.
              </p>
              <p>
                From startups and corporate headquarters to manufacturing facilities, retail offices and co-working spaces, we deliver customised branding solutions that leave a lasting impression.
              </p>
            </div>
          </div>

          {/* Why Corporate Office Branding Matters */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6 mb-12">
            <h3 className="text-xl font-bold text-white">Why Corporate Office Branding Matters</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              A thoughtfully branded workplace goes beyond decoration. It communicates who you are, what you stand for and how you want your business to be remembered.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white text-xs block mb-1">Strengthen Brand Identity</strong>
                <p className="text-[11px] text-slate-400">Consistent branding throughout your office reinforces your company's visual identity and values.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white text-xs block mb-1">Create a Strong First Impression</strong>
                <p className="text-[11px] text-slate-400">Reception areas and client-facing spaces communicate professionalism and build trust from the moment visitors enter.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white text-xs block mb-1">Enhance Employee Engagement</strong>
                <p className="text-[11px] text-slate-400">A well-designed workspace promotes pride, motivation and a stronger sense of belonging among employees.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white text-xs block mb-1">Improve Customer Experience</strong>
                <p className="text-[11px] text-slate-400">Branded environments create memorable experiences that enhance customer confidence and loyalty.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                <strong className="text-white text-xs block mb-1">Reinforce Company Culture</strong>
                <p className="text-[11px] text-slate-400">Office branding reflects your mission, vision and organisational values, helping employees connect with your purpose every day.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Wall Graphics & Environmental Branding */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Wall Graphics & Environmental Branding</h3>
              <h4 className="text-xs font-semibold text-amber-300">Turn Empty Walls into Brand Storytelling Spaces</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Walls provide valuable opportunities to showcase your company's journey, achievements and culture. Our solutions include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Inspirational Quote Walls",
                  "Company Timeline Displays",
                  "Brand Story Murals",
                  "Mission & Vision Walls",
                  "Core Values Graphics",
                  "Achievement & Awards Displays",
                  "Infographics",
                  "Motivational Employee Graphics",
                  "Custom Wall Murals",
                  "Vinyl Wall Graphics",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                These installations transform ordinary office spaces into engaging brand experiences.
              </p>
            </div>

            {/* Meeting Room & Conference Room Branding */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Meeting Room & Conference Room Branding</h3>
              <h4 className="text-xs font-semibold text-amber-300">Spaces That Inspire Collaboration</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Meeting rooms should reflect professionalism while supporting creativity and innovation. Our branding solutions include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Frosted Glass Branding",
                  "Conference Room Graphics",
                  "Custom Wall Art",
                  "Branded Presentation Walls",
                  "Digital Signage",
                  "Room Naming Systems",
                  "Glass Vinyl Graphics",
                  "Acoustic Graphic Panels",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                These enhancements create a polished and inspiring environment for internal meetings and client presentations.
              </p>
            </div>

            {/* Workspace & Employee Area Branding */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Workspace & Employee Area Branding</h3>
              <h4 className="text-xs font-semibold text-amber-300">Reinforce Company Culture Every Day</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Employee workspaces should motivate, inspire and reflect your organisation's values. Our workspace branding includes:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Department Branding",
                  "Team Zone Graphics",
                  "Employee Recognition Walls",
                  "Workspace Graphics",
                  "Desk Branding",
                  "Collaboration Zone Graphics",
                  "Innovation Walls",
                  "Learning Spaces",
                  "Wellness Area Branding",
                  "Cafeteria Graphics",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                A visually engaging workplace contributes to higher morale, productivity and employee satisfaction.
              </p>
            </div>

            {/* Glass Branding & Frosted Films */}
            <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4">
              <h3 className="text-xl font-bold text-white">Glass Branding & Frosted Films</h3>
              <h4 className="text-xs font-semibold text-amber-300">Modern Privacy with Professional Branding</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Glass partitions provide an excellent opportunity to combine privacy with elegant branding. We offer:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-300">
                {[
                  "Frosted Vinyl Graphics",
                  "Branded Glass Films",
                  "Privacy Patterns",
                  "Corporate Logo Application",
                  "Decorative Glass Designs",
                  "Conference Room Branding",
                  "Cabin Branding",
                  "Safety Graphics",
                ].map((item) => (
                  <div key={item} className="p-2 rounded bg-white/5 flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] font-mono text-slate-400 pt-2 border-t border-white/10">
                These solutions enhance both aesthetics and functionality without compromising natural light.
              </p>
            </div>
          </div>

          {/* Corporate Environmental Graphics */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-4 mb-12">
            <h3 className="text-xl font-bold text-white">Corporate Environmental Graphics</h3>
            <h4 className="text-xs font-semibold text-amber-300">Bring Your Brand to Life Throughout the Workplace</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              Environmental graphics help create immersive spaces that communicate your organisation's personality. Our services include:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 text-xs text-slate-300">
              {[
                "Large Format Graphics",
                "3D Installations",
                "Brand Murals",
                "Feature Walls",
                "Interactive Displays",
                "LED Branding Elements",
                "Illuminated Signage",
                "Digital Experience Zones",
                "Product Showcase Areas",
                "Innovation Centres",
              ].map((item) => (
                <div key={item} className="p-2.5 rounded-xl bg-white/5 border border-white/10 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
            <p className="text-xs font-mono text-slate-400 pt-2">
              These installations help create memorable workplace experiences for employees and visitors alike.
            </p>
          </div>

          {/* Our Office Branding Process */}
          <div className="p-8 rounded-3xl bg-white/[0.02] border border-white/10 space-y-6">
            <h3 className="text-xl font-bold text-white">Our Office Branding Process</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              Every workplace is unique, which is why we follow a structured process to ensure every branding project aligns with your business objectives.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">1. Discovery & Consultation</strong>
                <p className="text-[11px] text-slate-400">Understanding your brand identity, office layout, culture and goals.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">2. Site Survey</strong>
                <p className="text-[11px] text-slate-400">Evaluating available space, architecture, lighting and visitor flow.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">3. Concept Development</strong>
                <p className="text-[11px] text-slate-400">Developing creative concepts, mood boards and visual mock-ups.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">4. Design & Visualisation</strong>
                <p className="text-[11px] text-slate-400">Preparing detailed artwork, 3D visualisations and material specs.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">5. Production</strong>
                <p className="text-[11px] text-slate-400">Manufacturing high-quality branding elements using premium materials.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">6. Installation</strong>
                <p className="text-[11px] text-slate-400">Seamless execution with minimal disruption to daily operations.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-1">
                <strong className="text-amber-400 text-xs block">7. Quality Assurance</strong>
                <p className="text-[11px] text-slate-400">Final inspection to ensure exceptional finish and brand standards.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation & Lead Form */}
      <section id="strategy-form" className="py-20 md:py-28 relative">
        <div className="container-x">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-tr from-pink-950/40 via-[#0d0d1a] to-amber-950/30 border border-white/20 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
            <div className="glow-orb -top-20 -right-20 h-64 w-64 bg-pink-500 opacity-20" />

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-4 mb-10">
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-pink-400">
                Transform Your Brand Today
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white">
                Book Your Branding Strategy Session
              </h2>
              <p className="text-slate-300 text-sm">
                Discuss your brand identity, logo, video production, campaign design, or office branding with our senior creative strategists.
              </p>
            </div>

            {formSubmitted ? (
              <div className="p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-center space-y-4">
                <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
                <h3 className="text-xl font-bold text-white">Strategy Request Received!</h3>
                <p className="text-slate-300 text-sm">
                  Our senior creative director will reach out within 24 hours to schedule your strategy session.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setFormSubmitted(true);
                }}
                className="space-y-6 relative z-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Your Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Rahul Sharma"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Company / Brand Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Acme Brands"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="name@company.com"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono text-slate-300 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Primary Creative Need *</label>
                  <select
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-[#0e0e1a] border border-white/15 text-white text-sm focus:outline-none focus:border-pink-500 transition"
                  >
                    <option value="Brand Strategy & Identity">Brand Strategy & Complete Identity</option>
                    <option value="Logo Design & Visual Identity">Logo Design & Visual Guidelines</option>
                    <option value="Motion Graphics & AI Video">Motion Graphics & AI Video Creation</option>
                    <option value="Commercial Video Production">Commercial Video Production (TVC/Corporate)</option>
                    <option value="Product & Corporate Photography">Product & Corporate Photography</option>
                    <option value="Copywriting & SEO Content">Creative Copywriting & SEO Content</option>
                    <option value="360° Campaign Design">360-Degree Integrated Campaign Design</option>
                    <option value="Corporate Office Branding">Corporate Office & Environmental Branding</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-300 mb-2">Project Brief / Goals</label>
                  <textarea
                    rows={4}
                    placeholder="Tell us about your brand goals, target timeline, or existing creative challenges..."
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-pink-500 transition"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-600 to-amber-500 text-white font-bold text-sm shadow-xl hover:shadow-[0_0_40px_rgba(236,72,153,0.5)] transition-all flex items-center justify-center gap-2"
                >
                  <Send className="h-4 w-4" />
                  <span>Request Creative Strategy Call</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
